import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import StudentMessageThread from '../components/StudentMessageThread';
import { useAuth } from '../context/AuthContext';

function messagesPath(studentId, extra = {}) {
  const next = { ...extra };
  if (studentId) next.student_id = String(studentId);
  return next;
}

export default function MyMessagesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [children, setChildren] = useState([]);
  const [childrenLoaded, setChildrenLoaded] = useState(!user?.is_parent);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const studentId = searchParams.get('student_id') || '';
  const teacherId = searchParams.get('teacher_id') || '';
  const isParent = Boolean(user?.is_parent);

  useEffect(() => {
    if (!isParent) {
      setChildren([]);
      setChildrenLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setChildrenLoaded(false);
    (async () => {
      try {
        const res = await api.get('/parent/children/');
        if (!cancelled) {
          const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
          setChildren(list);
        }
      } catch {
        if (!cancelled) setChildren([]);
      } finally {
        if (!cancelled) setChildrenLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isParent]);

  useEffect(() => {
    if (!isParent || !childrenLoaded || studentId || children.length === 0) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('student_id', String(children[0].id));
      return next;
    }, { replace: true });
  }, [isParent, childrenLoaded, studentId, children, setSearchParams]);

  useEffect(() => {
    if (isParent && !childrenLoaded) return undefined;
    if (isParent && !studentId) {
      setLoading(!childrenLoaded || children.length > 0);
      setThreads([]);
      setError('');
      return undefined;
    }
    if (isParent && childrenLoaded && children.length === 0) {
      setLoading(false);
      setThreads([]);
      setError('');
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (studentId) params.student_id = studentId;
        const res = await api.get('/messaging/me/threads/', { params });
        if (!cancelled) {
          setThreads(res.data?.threads || []);
          if (!teacherId && (res.data?.threads || []).length === 1) {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('teacher_id', String(res.data.threads[0].teacher_id));
              return next;
            }, { replace: true });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Не удалось загрузить переписку');
          setThreads([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, teacherId, setSearchParams, isParent, childrenLoaded, children.length]);

  const active = threads.find((t) => String(t.teacher_id) === String(teacherId));
  const parentHasNoChildren = isParent && childrenLoaded && children.length === 0;
  const scheduleTo = studentId ? `/my-schedule?student_id=${studentId}` : '/my-schedule';

  return (
    <div className="sched-page">
      <div className="page-header">
        <h1>Сообщения</h1>
        <p className="page-subtitle">
          Организационная лента с преподавателем на сайте. Внешние мессенджеры — по желанию.
        </p>
      </div>

      {isParent && children.length > 0 && (
        <label className="sched-filter">
          Ребёнок
          <select
            value={studentId || String(children[0].id)}
            onChange={(e) => {
              const v = e.target.value;
              if (v) setSearchParams({ student_id: v });
            }}
          >
            {children.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name || c.first_name || c.username}
              </option>
            ))}
          </select>
        </label>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p>Загрузка…</p>}

      {!loading && parentHasNoChildren && (
        <p className="muted">
          Пока нет привязанных детей. Нужна секретная ссылка от ученика или преподавателя —
          откройте её и войдите или зарегистрируйтесь.
          {' '}
          <Link to="/parent">Кабинет родителя</Link>
        </p>
      )}

      {!loading && !parentHasNoChildren && threads.length === 0 && !error && (
        <p className="muted">Нет активных преподавателей для переписки.</p>
      )}

      {!loading && threads.length > 0 && (
        <div className="ops-messages-layout">
          <aside className="ops-messages-aside">
            <ul className="ops-thread-picker">
              {threads.map((t) => (
                <li key={t.thread_id}>
                  <button
                    type="button"
                    className={
                      String(t.teacher_id) === String(teacherId)
                        ? 'ops-thread-picker-item is-active'
                        : 'ops-thread-picker-item'
                    }
                    onClick={() => {
                      setSearchParams(messagesPath(studentId, { teacher_id: String(t.teacher_id) }));
                    }}
                  >
                    <span>{t.teacher_name}</span>
                    {t.unread_count > 0 && (
                      <span className="badge">{t.unread_count}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            <p className="muted">
              <Link to={scheduleTo}>К расписанию</Link>
              {' — перенос и отмена отдельной формой.'}
            </p>
          </aside>
          <div className="ops-messages-main">
            {active ? (
              <StudentMessageThread
                mode="student"
                teacherId={active.teacher_id}
                studentId={studentId || undefined}
              />
            ) : (
              <p className="muted">Выберите преподавателя слева.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
